/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDSelect from 'components/MDSelect';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';

function BranchSwitcher({ brandName, textColor, onBranchChange }) {
  const { t } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [canChange, setCanChange] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [loading, setLoading] = useState(false);

  const loadBranches = () => {
    FetchData('OPTIONS', '/profile/changebranch', app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        const list = (result.branches || []).map((item) => ({
          id: String(item.id),
          name: item.branch_name ? `${item.name} - ${item.branch_name}` : item.name,
        }));
        setBranches(list);
        setCanChange(result.can_change_branch === 'Y');
        setBranchId(String(result.branch_id || app.user.branch_id || ''));
      }
    });
  };

  useEffect(() => {
    loadBranches();
  }, [app.token, app.user.branch_id]);

  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canChange) return;
    loadBranches();
    setOpen(true);
  };

  const handleSave = () => {
    if (!branchId || branchId === String(app.user.branch_id)) {
      setOpen(false);
      return;
    }

    setLoading(true);
    FetchData('PATCH', '/profile/changebranch', app.token, {
      lang: app.lang,
      branch: branchId,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setOpen(false);
        if (onBranchChange) {
          onBranchChange(result.user);
        }
        Swal.fire({
          icon: 'success',
          title: t('alert.savecomplete'),
          timer: 1200,
          showConfirmButton: false,
        }).then(() => {
          const home = app.home || '/';
          window.location.assign(home);
        });
        return;
      }

      Swal.fire({
        icon: 'error',
        title: t('alert.saveerror'),
        text: t(`alert.${result.msg}`, { defaultValue: result.msg }),
      });
    });
  };

  return (
    <>
      <MDBox
        onClick={handleOpen}
        sx={{
          cursor: canChange ? 'pointer' : 'default',
          display: 'inline-flex',
          alignItems: 'center',
          maxWidth: '100%',
          verticalAlign: 'middle',
          gap: 0.25,
          '&:hover': canChange ? { opacity: 0.85 } : {},
        }}
      >
        <MDTypography
          component='h6'
          variant='button'
          fontWeight='medium'
          color={textColor}
          sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}
        >
          {brandName}
        </MDTypography>
        {canChange && (
          <Icon sx={{ fontSize: '1rem', color: textColor, opacity: 0.8, flexShrink: 0 }}>
            swap_horiz
          </Icon>
        )}
      </MDBox>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>{t('menu.branch_change')}</DialogTitle>
        <DialogContent>
          <MDBox pt={1}>
            <MDSelect
              label={t('menu.branch')}
              labelDefault={t('element.select')}
              value={branchId}
              data={branches}
              onChange={(e) => setBranchId(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='text' color='secondary' onClick={() => setOpen(false)}>
            {t('alert.cancel')}
          </MDButton>
          <MDButton variant='gradient' color='info' onClick={handleSave} disabled={loading}>
            {t('alert.ok')}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

BranchSwitcher.defaultProps = {
  onBranchChange: null,
};

BranchSwitcher.propTypes = {
  brandName: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  onBranchChange: PropTypes.func,
};

export default BranchSwitcher;
